import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle2,
  ChevronDown,
  CloudOff,
  Loader2,
  LogIn,
  RefreshCw,
  Ticket,
  XCircle,
} from "lucide-react";
import JoinedBetInspector from "../components/joined-bets/JoinedBetInspector";
import JoinedBetListRow from "../components/joined-bets/JoinedBetListRow";
import JoinedBetsChrome from "../components/joined-bets/JoinedBetsChrome";
import JoinedBetsFilterNav from "../components/joined-bets/JoinedBetsFilterNav";
import JoinedBetsStatusBar from "../components/joined-bets/JoinedBetsStatusBar";
import { selectAppUser } from "../redux/appAuthSlice";
import { fetchJoinedBets } from "../redux/joinedBetsSlice";
import {
  countTicketsByStatus,
  filterJoinedBets,
  getBetHouseName,
  normalizeTicketStatus,
  sumPotential,
  sumStaked,
} from "../utils/joinedBetsHelpers";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useHorizontalPanelResize } from "../hooks/useHorizontalPanelResize";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import "../joined-bets.css";

const BET_HOUSE_HOME = "/app/bethouse";
const BASE_PATH = "/app/joined-bets";
const RAIL_STACK_BREAKPOINT = "(max-width: 960px)";
const RAIL_MOBILE_INITIAL = 6;
const RAIL_MOBILE_STEP = 6;
const JB_RAIL_DEFAULT = 272;
const JB_RAIL_MIN = 200;
const JB_RAIL_MAX = 480;

const JoinedBets = () => {
  const { ticketId: routeTicketId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const userId = user?._id;
  const { bets, status, error } = useSelector((s) => s.joinedBets);

  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState(null);
  const [railVisibleCount, setRailVisibleCount] = useState(RAIL_MOBILE_INITIAL);
  const panelRef = useRef(null);
  const isStackedLayout = useMediaQuery(RAIL_STACK_BREAKPOINT);
  const {
    size: railWidth,
    setClampedSize: setRailWidth,
    reset: resetRailWidth,
    minSize: railMin,
    maxSize: railMax,
  } = usePersistedPanelSize("bb-jb-rail-w", {
    defaultSize: JB_RAIL_DEFAULT,
    minSize: JB_RAIL_MIN,
    maxSize: JB_RAIL_MAX,
  });
  const onRailResizePointerDown = useHorizontalPanelResize(
    railWidth,
    setRailWidth,
    "start"
  );

  const load = useCallback(() => {
    if (!userId) return Promise.resolve();
    return dispatch(fetchJoinedBets(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const filtered = useMemo(() => {
    let list = filterJoinedBets(bets, activeFilter).filter((b) => b && b._id);
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((bet) => {
      const house = getBetHouseName(bet).toLowerCase();
      const legs = (bet.fixtures || []).some((item) => {
        const fx = item?.fixture;
        const home = fx?.teams?.home?.name?.toLowerCase() || "";
        const away = fx?.teams?.away?.name?.toLowerCase() || "";
        return home.includes(q) || away.includes(q);
      });
      return house.includes(q) || legs;
    });
  }, [bets, activeFilter, search]);

  const filterCounts = useMemo(() => countTicketsByStatus(bets), [bets]);

  const openCount = useMemo(
    () =>
      (Array.isArray(bets) ? bets : []).filter((bet) =>
        ["pending", "active"].includes(normalizeTicketStatus(bet?.status))
      ).length,
    [bets]
  );

  const portfolioStaked = useMemo(() => sumStaked(bets), [bets]);
  const portfolioPotential = useMemo(() => sumPotential(bets), [bets]);

  const selectTicket = useCallback(
    (id) => {
      setSelectedId(id);
      if (id) {
        navigate(`${BASE_PATH}/${id}`, { replace: true });
      } else {
        navigate(BASE_PATH, { replace: true });
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!filtered.length) {
      if (selectedId) selectTicket(null);
      return;
    }
    const routeMatch = routeTicketId
      ? filtered.find((b) => String(b._id) === String(routeTicketId))
      : null;
    const selectedMatch = selectedId
      ? filtered.find((b) => String(b._id) === String(selectedId))
      : null;

    if (routeMatch) {
      if (String(selectedId) !== String(routeMatch._id)) {
        setSelectedId(routeMatch._id);
      }
      return;
    }
    if (selectedMatch) return;
    selectTicket(filtered[0]._id);
  }, [filtered, routeTicketId, selectedId, selectTicket]);

  const selectedBet = useMemo(
    () => filtered.find((b) => String(b._id) === String(selectedId)) || null,
    [filtered, selectedId]
  );

  useEffect(() => {
    setRailVisibleCount(RAIL_MOBILE_INITIAL);
  }, [activeFilter, search]);

  const visibleTickets = useMemo(() => {
    if (!isStackedLayout) return filtered;

    const limit = railVisibleCount;
    const selectedIdx = selectedId
      ? filtered.findIndex((b) => String(b._id) === String(selectedId))
      : -1;

    if (selectedIdx < 0 || selectedIdx < limit) {
      return filtered.slice(0, limit);
    }

    const selected = filtered[selectedIdx];
    const rest = filtered
      .filter((_, index) => index !== selectedIdx)
      .slice(0, limit - 1);
    return [selected, ...rest];
  }, [filtered, isStackedLayout, railVisibleCount, selectedId]);

  const railRemainingCount = isStackedLayout
    ? Math.max(0, filtered.length - railVisibleCount)
    : 0;

  const onToast = (type, title, detail) => setToast({ type, title, detail });

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!selectedId) return;
    if (typeof window === "undefined" || window.innerWidth > 960) return;
    const timer = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [selectedId]);

  const totalStaked = sumStaked(filtered);
  const totalPotential = sumPotential(filtered);
  const workspaceStyle = !isStackedLayout
    ? { "--jb-rail-w": `${railWidth}px` }
    : undefined;

  if (!userId) {
    return (
      <div className="jb-page">
        <div className="jb-state">
          <LogIn size={40} />
          <h1>Sign in to see your tickets</h1>
          <Link to="/app/login" className="jb-primary-btn">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  if (status === "loading" && !bets.length) {
    return (
      <div className="jb-page">
        <div className="jb-state">
          <Loader2 size={28} className="animate-spin" />
          <p>Loading your tickets…</p>
        </div>
      </div>
    );
  }

  if (status === "failed" && !bets.length) {
    return (
      <div className="jb-page">
        <div className="jb-state">
          <CloudOff size={40} />
          <p>{String(error || "Could not load bets.")}</p>
          <button type="button" className="jb-primary-btn" onClick={onRefresh}>
            <RefreshCw size={15} />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="jb-page">
      <div className="jb-desk">
        <div className="jb-desk-chrome">
          <JoinedBetsChrome
            ticketCount={filtered.length}
            totalStaked={totalStaked}
            totalPotential={totalPotential}
            refreshing={refreshing}
            onRefresh={onRefresh}
            search={search}
            onSearchChange={setSearch}
          />
        </div>

        <div className="jb-desk-workspace" style={workspaceStyle}>
          <div className="jb-desk-rail-wrap">
            <aside className="jb-desk-rail" aria-label="Your tickets">
              <div className="jb-rail-panel">
                <JoinedBetsFilterNav
                  openCount={openCount}
                  totalStaked={portfolioStaked}
                  totalPotential={portfolioPotential}
                  resultCount={filtered.length}
                />
                <div className="jb-rail-slips">
                  <div className="jb-rail-head">
                    <strong>Slips</strong>
                    <span className="tabular-nums">{filtered.length}</span>
                  </div>
                  <div className="jb-rail-list">
                    {filtered.length ? (
                      <>
                        {visibleTickets.map((bet) => (
                          <JoinedBetListRow
                            key={bet._id}
                            bet={bet}
                            selected={String(selectedId) === String(bet._id)}
                            onSelect={selectTicket}
                          />
                        ))}
                        {railRemainingCount > 0 ? (
                          <button
                            type="button"
                            className="jb-rail-load-more"
                            onClick={() =>
                              setRailVisibleCount(
                                (count) => count + RAIL_MOBILE_STEP
                              )
                            }
                          >
                            <ChevronDown size={14} aria-hidden />
                            Load more
                            <span className="tabular-nums">
                              {railRemainingCount} left
                            </span>
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <div className="jb-rail-empty">
                        <Ticket size={28} strokeWidth={1.25} />
                        <p>
                          {search.trim()
                            ? "No tickets match your search."
                            : activeFilter === "all"
                              ? "No tickets yet."
                              : "No tickets in this filter."}
                        </p>
                        {!search.trim() && activeFilter === "all" ? (
                          <Link to={BET_HOUSE_HOME} className="jb-link-btn">
                            Browse bet houses
                          </Link>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </aside>
            {!isStackedLayout ? (
              <div
                className="jb-resize-handle"
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={railWidth}
                aria-valuemin={railMin}
                aria-valuemax={railMax}
                aria-label="Resize slips panel"
                title="Drag to resize · double-click to reset"
                onPointerDown={onRailResizePointerDown}
                onDoubleClick={resetRailWidth}
              />
            ) : null}
          </div>

          <section
            ref={panelRef}
            className="jb-desk-panel"
            aria-label="Ticket detail"
          >
            <JoinedBetInspector
              bet={selectedBet}
              onToast={onToast}
              onDeleted={() => selectTicket(null)}
              statusBar={
                <JoinedBetsStatusBar
                  activeFilter={activeFilter}
                  onFilterChange={setActiveFilter}
                  filterCounts={filterCounts}
                />
              }
            />
          </section>
        </div>
      </div>

      {toast ? (
        <div className={`jb-toast jb-toast-${toast.type}`} role="status">
          {toast.type === "error" ? (
            <XCircle size={16} aria-hidden />
          ) : (
            <CheckCircle2 size={16} aria-hidden />
          )}
          <span>
            <strong>{toast.title}</strong>
            {toast.detail ? <em>{toast.detail}</em> : null}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default JoinedBets;
